import React, { useEffect, useState } from "react";
import {
  Gift,
  Users,
  UserPlus,
  Copy,
  Share2,
  CheckCircle2,
  Clock3,
} from "lucide-react";

import { useLazyGetMyReferralsQuery } from "../../redux/actions/Customer/profileCustomer";
import { REFERRAL_BASE_URL } from "../../config/apiUrlsCustomer";
import { ImagePath } from "../../assests";

// ============================================================
// Interfaces
// ============================================================

export interface ReferralUser {
  userId: number;
  customerName: string;
  profileImageUrl?: string | null;
  joinedOn: string;
  isFirstOrderCompleted: boolean;
  firstOrderAmount: number;
  referralBonusEarned: boolean;
  referralBonusAmount: number;
  status: string;
}

export interface MyReferralResponse {
  referralCode: string;
  referralLink: string;
  registrationBonus: number;
  firstOrderBonus: number;
  totalReferralBonusEarned: number;
  totalReferredUsers: number;
  completedReferrals: number;
  pendingReferrals: number;
  referrals: ReferralUser[];
}

// ============================================================
// Component
// ============================================================

const ReferralDashboardView: React.FC = () => {
  const [fetchReferrals, { data, isLoading, isError }] =
    useLazyGetMyReferralsQuery();

  const [copied, setCopied] = useState(false);

  // ==========================================================
  // Fetch Referral Details
  // ==========================================================

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  // ==========================================================
  // Safely Extract Referral Data
  // ==========================================================

  const rawData: any = data;

  const referralData: MyReferralResponse | undefined =
    rawData?.value?.data ?? rawData?.data ?? rawData;

  // ==========================================================
  // Format Date
  // ==========================================================

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ==========================================================
  // Copy Referral Link
  // ==========================================================

  const handleCopy = async () => {
    if (!referralData?.referralLink) return;
 const link = `${REFERRAL_BASE_URL}/?ref=${referralData.referralCode}`;
    try {
      await navigator.clipboard.writeText(link);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy referral link:", error);
    }
  };

  // ==========================================================
  // Share Referral
  // ==========================================================

  const handleShare = async () => {
    if (!referralData?.referralLink) return;

    const link = `${REFERRAL_BASE_URL}/?ref=${referralData.referralCode}`;

    const shareData = {
      title: "Join Petland",
      text: `Join Petland using my referral code ${referralData.referralCode}`,
      url: link,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(link);

        setCopied(true);

        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
    } catch (error) {
      console.log("Share cancelled.");
    }
  };

  // ==========================================================
  // Loading State
  // ==========================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2E5B37]" />
      </div>
    );
  }

  // ==========================================================
  // Error State
  // ==========================================================

  if (isError) {
    return (
      <div className="bg-red-50 text-red-600 p-6 rounded-3xl text-center max-w-xl mx-auto">
        <p className="font-semibold">
          Unable to fetch referral details.
        </p>

        <button
          onClick={() => fetchReferrals()}
          className="mt-3 px-4 py-2 bg-[#2E5B37] text-white rounded-xl text-xs font-bold"
        >
          Try Again
        </button>
      </div>
    );
  }

  // ==========================================================
  // Main UI
  // ==========================================================

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 pb-16">

      {/* ======================================================
          REFERRAL BANNER
      ======================================================= */}

      <section className="relative w-full">

        {/* Banner Image */}
        <div className="w-full overflow-hidden rounded-[24px] sm:rounded-[28px]">
          <img
            src={ImagePath.refferalBanner}
            alt="Refer and Earn"
            className="block w-full h-auto object-cover"
          />
        </div>


      </section>

      {/* ======================================================
          REFERRAL CODE & SHARE

          Small gap from banner
      ======================================================= */}

      <section
        className="
          mt-2
          bg-[#2E5B37]
          rounded-[22px]
          sm:rounded-[24px]
          p-4
          sm:p-5
        "
      >
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">

          {/* ==================================================
              REFERRAL CODE
          ================================================== */}

          <div className="flex-1 min-w-0">

            <p className="text-[10px] sm:text-xs font-medium text-white/90 mb-2">
              Your referral code
            </p>

            <div className="h-[52px] bg-white rounded-xl px-4 flex items-center justify-between">

              <span className="font-extrabold tracking-[0.2em] text-gray-900 text-sm sm:text-base">
                {referralData?.referralCode ?? "---"}
              </span>

              <button
                onClick={handleCopy}
                className="
                  text-[#2E5B37]
                  hover:bg-gray-100
                  p-2
                  rounded-lg
                  transition
                "
                title="Copy referral link"
              >
                {copied ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* ==================================================
              SHARE BUTTON
          ================================================== */}

          <button
            onClick={handleShare}
            className="
              h-[52px]
              w-full
              sm:w-[170px]
              px-5
              rounded-xl
              bg-white
              text-[#2E5B37]
              font-bold
              text-sm
              flex
              items-center
              justify-center
              gap-2
              hover:bg-emerald-50
              transition
              shrink-0
            "
          >
            <Share2 className="w-4 h-4" />
            Share Referral
          </button>
        </div>

        {copied && (
          <p className="text-[11px] text-white/90 mt-2">
            Referral link copied!
          </p>
        )}
      </section>

      {/* ======================================================
          STAT CARDS
      ======================================================= */}

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-5">

        {/* ==================================================
            TOTAL EARNING
        ================================================== */}

        <div className="bg-white border border-gray-100 rounded-[22px] p-4 sm:p-5 shadow-sm">

          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>

          <p className="text-[10px] sm:text-xs text-gray-400 mt-3 sm:mt-4">
            Total Earning
          </p>

          <p className="text-lg sm:text-xl font-extrabold text-gray-900 mt-1">
            AED{" "}
            {(referralData?.totalReferralBonusEarned ?? 0).toFixed(2)}
          </p>

          <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
            Referral rewards earned
          </p>
        </div>


        {/* ==================================================
            TOTAL REFERRALS
        ================================================== */}

        <div className="bg-white border border-gray-100 rounded-[22px] p-4 sm:p-5 shadow-sm">

          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center">
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>

          <p className="text-[10px] sm:text-xs text-gray-400 mt-3 sm:mt-4">
            Total Referrals
          </p>

          <p className="text-lg sm:text-xl font-extrabold text-gray-900 mt-1">
            {referralData?.totalReferredUsers ?? 0}
          </p>

          <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
            Friends invited
          </p>
        </div>

        {/* ==================================================
            COMPLETED REFERRALS
        ================================================== */}

        <div className="bg-white border border-gray-100 rounded-[22px] p-4 sm:p-5 shadow-sm">

          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center">
            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>

          <p className="text-[10px] sm:text-xs text-gray-400 mt-3 sm:mt-4">
            Completed Referrals
          </p>

          <p className="text-lg sm:text-xl font-extrabold text-gray-900 mt-1">
            {referralData?.completedReferrals ?? 0}
          </p>

          <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
            First order completed
          </p>
        </div>
      </section>

      {/* ======================================================
          REFERRAL PROGRESS
      ======================================================= */}

      <section className="mt-5 bg-white border border-gray-100 rounded-[24px] p-4 sm:p-6 shadow-sm">

        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">

          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Referral Progress
            </h2>

            <p className="text-xs text-gray-400 mt-1">
              Track your invited friends
            </p>
          </div>

          {/* ==================================================
              COUNTS
          ================================================== */}

          <div className="flex items-center gap-4 text-xs">

            <div className="flex items-center gap-1.5 text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />

              <span>
                {referralData?.completedReferrals ?? 0} Completed
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-amber-600">
              <Clock3 className="w-4 h-4" />

              <span>
                {referralData?.pendingReferrals ?? 0} Pending
              </span>
            </div>
          </div>
        </div>

        {/* ==================================================
            REFERRAL LIST
        ================================================== */}

        <div className="mt-5 space-y-2.5">

          {referralData?.referrals &&
          referralData.referrals.length > 0 ? (

            referralData.referrals.map((referral) => {

              const completed =
                referral.isFirstOrderCompleted;

              return (
                <div
                  key={referral.userId}
                  className="
                    flex
                    flex-col
                    sm:flex-row
                    sm:items-center
                    sm:justify-between
                    gap-3
                    p-3.5
                    sm:p-4
                    rounded-2xl
                    border
                    border-gray-100
                    hover:bg-gray-50
                    transition-colors
                  "
                >

                  {/* ==================================================
                      USER INFORMATION
                  ================================================== */}

                  <div className="flex items-center gap-3 min-w-0">

                    {/* Profile Image */}
                    {referral.profileImageUrl ? (
                      <img
                        src={referral.profileImageUrl}
                        alt={
                          referral.customerName ||
                          "Customer"
                        }
                        className="
                          w-11
                          h-11
                          sm:w-12
                          sm:h-12
                          rounded-full
                          object-cover
                          shrink-0
                          border
                          border-gray-100
                        "
                      />
                    ) : (
                      <div
                        className="
                          w-11
                          h-11
                          sm:w-12
                          sm:h-12
                          rounded-full
                          bg-emerald-50
                          text-emerald-700
                          flex
                          items-center
                          justify-center
                          shrink-0
                        "
                      >
                        <UserPlus className="w-5 h-5" />
                      </div>
                    )}

                    {/* User Details */}
                    <div className="min-w-0">

                      <p className="text-sm font-bold text-gray-900 truncate">
                        {referral.customerName ||
                          "Petland User"}
                      </p>

                      <p className="text-xs text-gray-400 mt-0.5">
                        Joined{" "}
                        {formatDate(
                          referral.joinedOn
                        )}
                      </p>

                      {completed && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          First order: AED{" "}
                          {(
                            referral.firstOrderAmount ??
                            0
                          ).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ==================================================
                      EARNINGS + STATUS
                  ================================================== */}

                  <div className="flex items-center justify-between sm:justify-end gap-4 pl-14 sm:pl-0">

                    {/* Earnings */}
                    {referral.referralBonusEarned ? (
                      <div className="text-right">

                        <p className="text-sm font-extrabold text-emerald-600">
                          + AED{" "}
                          {(
                            referral.referralBonusAmount ??
                            0
                          ).toFixed(2)}
                        </p>

                        <p className="text-[10px] text-gray-400">
                          Earnings
                        </p>
                      </div>
                    ) : (
                      <div className="text-right">

                        <p className="text-xs font-semibold text-gray-400">
                          —
                        </p>

                        <p className="text-[10px] text-gray-400">
                          Earnings
                        </p>
                      </div>
                    )}

                    {/* Status */}
                    <div
                      className={`
                        px-3
                        py-1.5
                        rounded-xl
                        text-xs
                        font-bold
                        flex
                        items-center
                        gap-1.5
                        whitespace-nowrap
                        ${
                          completed
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-amber-50 text-amber-700"
                        }
                      `}
                    >

                      {completed ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <Clock3 className="w-3.5 h-3.5" />
                      )}

                      {referral.status}
                    </div>
                  </div>
                </div>
              );
            })

          ) : (

            /* ==================================================
               EMPTY STATE
            ================================================== */

            <div className="text-center py-12 sm:py-16">

              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>

              <p className="text-sm font-bold text-gray-900 mt-4">
                No referrals yet
              </p>

              <p className="text-xs text-gray-400 mt-1">
                Share your referral link and invite
                your friends.
              </p>

              <button
                onClick={handleShare}
                className="
                  mt-4
                  px-5
                  py-2.5
                  rounded-xl
                  bg-[#2E5B37]
                  text-white
                  text-xs
                  font-bold
                  flex
                  items-center
                  gap-2
                  mx-auto
                  hover:bg-[#244A2C]
                  transition
                "
              >
                <Share2 className="w-4 h-4" />
                Invite a Friend
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReferralDashboardView;